import React from "react"

import { useTranslation } from "react-i18next"

import { BonusViolation } from "services/bonus/type"

import { formatDateTime, formatFiatCurrency } from "util/formatter"

import { getDisplayName } from "../../helpers/bonus2.helpers"

interface ViolationRoundCardProps {
  violation: BonusViolation
  onDismiss: (violationId: string) => void
}

export const ViolationRoundCard: React.FC<ViolationRoundCardProps> = ({ violation: v, onDismiss }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const facts = [
    v.bet_amount && { key: "bet", label: t("bonus2.violations.betAmount"), value: formatFiatCurrency(Number(v.bet_amount)) },
    v.game_id && { key: "game", label: t("bonus2.violations.game"), value: v.game_id },
    { key: "status", label: t("bonus2.violations.playerBonusStatus"), value: v.player_bonus.status },
    { key: "bonus", label: t("bonus2.violations.bonusName"), value: getDisplayName(v.player_bonus.bonus.name) }
  ].filter(Boolean) as Array<{ key: string; label: string; value: string }>

  const caps =
    v.violation_type === "MAX_BET_EXCEEDED"
      ? ([
          v.player_bonus.bonus.max_bet_bonus && {
            key: "maxBetBonus",
            label: t("bonus2.violations.maxBetBonus"),
            value: `$${v.player_bonus.bonus.max_bet_bonus}`
          },
          v.player_bonus.bonus.max_bet_wagering && {
            key: "maxBetWagering",
            label: t("bonus2.violations.maxBetWagering"),
            value: `$${v.player_bonus.bonus.max_bet_wagering}`
          }
        ].filter(Boolean) as Array<{ key: string; label: string; value: string }>)
      : []

  return (
    <div className="rounded-lg border border-gray-700 bg-[#0C0C1A] p-3">
      <div className="mb-2 flex items-start justify-between gap-4">
        <div className="text-xs text-gray-400">{formatDateTime(v.created_at)}</div>
        <button
          onClick={() => onDismiss(v.id)}
          className="rounded bg-gray-600 px-2 py-1 text-xs text-white transition-colors hover:bg-gray-700"
        >
          {t("bonus2.violations.dismiss")}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
        {facts.map((f) => (
          <div key={f.key}>
            <div className="mb-1 text-gray-400">{f.label}</div>
            <div className="font-semibold text-white">{f.value}</div>
          </div>
        ))}
      </div>

      {caps.length > 0 && (
        <div className="mt-3 flex gap-4 border-t border-gray-700 pt-3 text-sm">
          {caps.map((c) => (
            <div key={c.key}>
              <span className="text-gray-400">{c.label} </span>
              <span className="font-semibold text-white">{c.value}</span>
            </div>
          ))}
        </div>
      )}

      {v.metadata && (
        <div className="mt-3 border-t border-gray-700 pt-3">
          <div className="mb-2 text-xs text-gray-400">{t("bonus2.violations.additionalDetails")}</div>
          <pre className="overflow-x-auto rounded bg-[#1A1A2E] p-3 text-xs text-gray-300">{JSON.stringify(v.metadata, null, 2)}</pre>
        </div>
      )}
    </div>
  )
}
